import type { API } from 'api/config/types';
import { Core } from 'chessboard/core/config/types';
import { getExtendedNode } from 'chessboard/model/game/get-extended-node';
import type { Game } from 'chessboard/model/game/types';
import { handleTimestamp } from 'chessboard/api/move/model/handle-timestamp';
import { formatAPIMove } from 'api/move/model/format-api-move';

/**
 * Create addMoveToEndOfLine API method.
 */
export function createAddMoveToEndOfLine(context: API.Context) {
  /**
   * Add a move to the end of the line without updating the current position.
   */
  return function addMoveToEndOfLine(mv: API.Options.Move, line = 0): API.Response {
    const { api, game, mutateGame, respond } = context;
    const { ids } = api.getSelectedNode() ?? { ids: { move: -1, line: 0 } };
    const { move: currentMove, line: currentLine } = ids;

    game.selectLineEnd(line);

    const formattedMove = formatAPIMove(mv, api.getFEN(), api.getVariant());

    const data =
      formattedMove !== null
        ? mutateGame(function callMethod() {
            // Make the move but don't follow any continuations,
            // as this moves always needs to be added to the
            // end of the specified line.
            game.move(formattedMove, false);

            if (formattedMove.time) {
              handleTimestamp({ context, time: formattedMove.time });
            }
          })
        : null;

    game.selectPosition(currentMove, currentLine);

    if (data === null) {
      return respond(Core.APIEvents.IllegalMove, { fen: game.fen(), move: formattedMove });
    }

    data.move =
      getExtendedNode({
        move: <Game.Move.Node>data.move,
        startingMoveNumber: game.startingMoveNumber(),
      }) || undefined;

    return respond(Core.APIEvents.AddMove, { listMove: data.move });
  };
}
